import { BrowserWindow, IPropsStore } from '@wix/thunderbolt-symbols'
import {
	getStateOverrideHints,
	StylableEditor,
	STYLABLE_OVERRIDE_STATE_PREFIX,
	StylesheetDriver,
} from '@wix/stylable-panel-drivers'
import { mapValues, reduce } from 'lodash'
import { IForceStateParams } from 'thunderbolt-viewer-manager-types'
import { DeclarationMap, ForceStateState, StylableApiState, TransformedQuickChange } from './types'

const getSheetPath = (styleId: string) => `/${styleId}.st.css`

const STYLABLE_DRIVER_STATE_SET_VALUE = 'true'
const stylableTempOverrides = document.createElement('div')
stylableTempOverrides.id = 'stylable-temp-overrides'

function addOverridesElementToDOM(window: NonNullable<BrowserWindow>) {
	if (!window.document.getElementById(stylableTempOverrides.id)) {
		window.document.body.appendChild(stylableTempOverrides)
	}
}

function quickChangeToDom(
	quickChanges: Array<TransformedQuickChange>,
	window: NonNullable<BrowserWindow>,
	compId: string
) {
	const elementId = `quick-change-style-${compId}`

	let quickChangeStyleElement = window.document.getElementById(elementId)
	if (!quickChangeStyleElement) {
		quickChangeStyleElement = window.document.createElement('style')
		quickChangeStyleElement.id = elementId
	}

	let css = '/*quick changes*/\n'
	quickChanges.forEach((quickChange) => {
		const rawDecls = Object.entries(quickChange.declarationMap)
		const decls = rawDecls
			.filter((decl) => !!decl[1])
			.map(([key, value]) => `${key}: ${value};\n`)
			.join('')
		css += `${quickChange.selector} {
            ${decls}}
        `
	})

	quickChangeStyleElement.textContent = css
	stylableTempOverrides.appendChild(quickChangeStyleElement)
	addOverridesElementToDOM(window)
}

function getTransformedSelector(sheetDriver: StylesheetDriver, preTransformSelector: string, compId: string) {
	const { selectors } = sheetDriver.getTargetRule(preTransformSelector, true)
	return selectors.map((selector) => `#${compId}${selector}, #${compId} ${selector}`).join(', ')
}

function overtakeEntireCssRule(rule: DeclarationMap) {
	return mapValues(rule, (declarationValue) => `${declarationValue} !important`)
}

function applyQuickChangeToDOM(
	stylableEditor: StylableEditor,
	sheetPath: string,
	compId: string,
	window: NonNullable<BrowserWindow>,
	state: StylableApiState
) {
	const quickChanges = state.quickChanges[compId]
	let transformedQuickChanges: Array<TransformedQuickChange> = []
	if (stylableEditor && sheetPath) {
		const sheetDriver = stylableEditor.stylableDriver.getStylesheet(sheetPath)

		if (quickChanges && sheetDriver) {
			transformedQuickChanges = reduce(
				quickChanges,
				(result, value, preTransformSelector) => {
					const newSelector = getTransformedSelector(sheetDriver, preTransformSelector, compId)
					const rule = overtakeEntireCssRule(value)

					result.push({
						selector: newSelector,
						declarationMap: rule,
					})
					return result
				},
				[] as Array<TransformedQuickChange>
			)
		}
	}

	quickChangeToDom(transformedQuickChanges, window, compId)
}

export const setQuickChangeInternal = (
	stylableEditor: StylableEditor,
	styleId: string,
	compId: string,
	selector: string,
	declarationMap: DeclarationMap,
	window: NonNullable<BrowserWindow>,
	state: StylableApiState
) => {
	let compQuickChange = state.quickChanges[compId]
	if (compQuickChange === undefined) {
		compQuickChange = state.quickChanges[compId] = {}
	}
	compQuickChange[selector] = declarationMap

	applyQuickChangeToDOM(stylableEditor, getSheetPath(styleId), compId, window, state)
}

export const revertQuickChangeInternal = (
	stylableEditor: StylableEditor,
	styleId: string,
	compId: string,
	window: NonNullable<BrowserWindow>,
	state: StylableApiState
) => {
	state.quickChanges[compId] = {}

	applyQuickChangeToDOM(stylableEditor, getSheetPath(styleId), compId, window, state)
}

const updateElementState = (compNode: HTMLElement, { selector, stateAttribute }: ForceStateState, enable: boolean) => {
	if (selector) {
		const elements = compNode.parentNode!.querySelectorAll(selector)
		if (elements && stateAttribute) {
			Array.from(elements).forEach((element: Element) => {
				if (enable) {
					element.setAttribute(stateAttribute, STYLABLE_DRIVER_STATE_SET_VALUE)
				} else {
					element.removeAttribute(stateAttribute)
				}
			})
		}
	}
}

/**
 *
 * @param transformedForceStateParams - params for
 * @param enable - applies forceState by specified parameters iff true, otherwise removes it
 */
const setForceStateToDom = (transformedForceStateParams: ForceStateState, enable: boolean) => {
	const rootElement: HTMLElement = window.document.body
	if (transformedForceStateParams && transformedForceStateParams.stateAttribute) {
		updateElementState(rootElement, transformedForceStateParams || {}, enable)
	}
}

const applyForceStateSelectionSelector = (props: IPropsStore, compId: string, forceState?: ForceStateState) => {
	const compProps = {
		forceState: { ...forceState },
	}

	props.update({ [compId]: compProps })
}

const getStateKey = (compId: string, styleId: string) => {
	return `${compId}_${styleId}`
}

export const forceStateInternal = (
	state: StylableApiState,
	props: IPropsStore,
	stylableEditor: StylableEditor,
	styleId: string,
	compId: string,
	forceStateParams: IForceStateParams
) => {
	const sheetPath = getSheetPath(styleId)
	if (stylableEditor && sheetPath) {
		const sheetDriver = stylableEditor.stylableDriver.getStylesheet(sheetPath)

		if (forceStateParams && sheetDriver) {
			let stateAttribute
			let selector
			if (forceStateParams.selector) {
				const overrideHints = getStateOverrideHints(forceStateParams.selector, STYLABLE_OVERRIDE_STATE_PREFIX)
				stateAttribute = overrideHints.stateAttribute
				selector = getTransformedSelector(sheetDriver, overrideHints.selectorToQuery, compId)
			}
			const key = getStateKey(compId, styleId)
			const prevForceState = state.forceState[key]
			const forceState = {
				...prevForceState, // not sure this is needed
				selectionSelector: forceStateParams.selectionSelector,
				selector,
				stateAttribute,
			}

			if (prevForceState) {
				setForceStateToDom(prevForceState, false) // turn off previous forceState
			}
			state.forceState[key] = forceState

			setForceStateToDom(forceState, true) // turn on new forceState
			applyForceStateSelectionSelector(props, compId, forceState)
		}
	}
}

export const revertForceStateInternal = (
	state: StylableApiState,
	props: IPropsStore,
	compId: string,
	styleId: string
) => {
	const key = getStateKey(compId, styleId)
	setForceStateToDom(state.forceState[key], false) // turn off previous forceState
	state.forceState[key] = {}
	applyForceStateSelectionSelector(props, compId, state.forceState[key])
}

const disableTransitionsSnippet = (selectors: Array<string>) => {
	const selectorsIncludingChildren: Array<string> = []
	selectors.forEach((selector) => {
		selectorsIncludingChildren.push(selector)
		selectorsIncludingChildren.push(`${selector} *`)
	})

	return `${selectorsIncludingChildren.join(', ')} { transition: unset !important;}`
}

export const handleCssTransitions = (
	stylableEditor: StylableEditor,
	styleId: string,
	compId: string,
	previewState: string,
	window: NonNullable<BrowserWindow>
) => {
	const elementId = `transition-${styleId}`

	let transitionStyle = window.document.getElementById(elementId)
	if (!transitionStyle) {
		transitionStyle = window.document.createElement('style')
		transitionStyle.id = elementId
	}

	let css = ''
	if (previewState === 'editor') {
		const sheetPath = getSheetPath(styleId)
		if (stylableEditor && sheetPath) {
			const sheetDriver = stylableEditor.stylableDriver.getStylesheet(sheetPath)

			if (sheetDriver) {
				const { selectors } = sheetDriver.getTargetRule('.root', true)

				css = disableTransitionsSnippet(selectors)
			}
		}
	}

	transitionStyle.textContent = css
	stylableTempOverrides.appendChild(transitionStyle)
	addOverridesElementToDOM(window)
}
